import { useEffect, useRef, useState } from "react";
import TimeInput from "./timeInput";
import { secondsToHms, hmsToSeconds, isValidTimeFormat } from "./utils";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import { getContentLocation } from "@/utils/utilFunctions/commmonFunctions";
import EndTrim from "./icons/endTrim.svg?react";
import PointerIcon from "./icons/seek1.svg?react";
import Seek1Icon from "./icons/seek3.svg?react";
import Seek2Icon from "./icons/seek2.svg?react";
import StartTrim from "./icons/startTrim.svg?react";
import { useClickOutside } from "../../utils";
import styles from "./player.module.scss";
import { PauseIcon, PlusIcon } from "lucide-react";
import { PlayIcon, ChevronDown } from "lucide-react";
import { Dropdown } from "react-bootstrap";
import { store } from "@/store";

interface Segment {
  id: string;
  start: number;
  end: number;
  name: string;
}

interface SlateOverlay {
  id: string;
  url: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  visible: boolean;
  isSlate: boolean;
  duration?: number;
  autoHide?: boolean;
  isPreRoll?: boolean;
  isPostRoll?: boolean;
}

interface TrimmerProps {
  trimStart: number;
  trimEnd: number;
  playerRef: React.RefObject<any>;
  playedDuration: number;
  totalDuration: number;
  OnTrim: (startTrim: number, endTrim: number) => void;
  pauseIcon: boolean;
  setPauseIcon: (v: boolean) => void;
  setHasUserDragged: React.MutableRefObject<boolean>;
  allowMultipleSegments?: boolean;
  onSegmentsChange?: (segments: Array<{ start: number; end: number }>) => void;
  slateOverlays?: SlateOverlay[];
  onSlateOverlaysChange?: (slateOverlays: SlateOverlay[]) => void;
  // New props for sequential playback
  preRollSlateId?: string;
  postRollSlateId?: string;
  onSequentialPlayback?: (playbackSequence: {
    type: "pre-slate" | "video-segments" | "post-slate";
    slateId?: string;
    segments?: Array<{ start: number; end: number }>;
    reset?: boolean;
  }) => void;
  originalVideoDuration?: number;
  // New prop for reset functionality
  onReset?: () => void;
  isPreset?: boolean;
  canvasOverlay?: any;
  preRollMedia?: any;
  postRollMedia?: any;
  duration?: number;
  item?: any;
  getTotalDuration?: any;
  isMuted?: boolean;
  handleMute?: any;
  readOnlyMode?: boolean;
}

const noop = () => undefined;

const Trimmer = ({
  trimStart,
  trimEnd,
  playerRef,
  playedDuration,
  totalDuration,
  OnTrim,
  pauseIcon,
  setPauseIcon,
  allowMultipleSegments = false,
  setHasUserDragged,
  onSegmentsChange,
  slateOverlays = [],
  onSlateOverlaysChange,
  preRollSlateId,
  postRollSlateId,
  onSequentialPlayback,
  originalVideoDuration,
  onReset,
  isPreset = false,
  canvasOverlay,
  preRollMedia,
  postRollMedia,
  duration,
  item,
  getTotalDuration,
  isMuted = true,
  handleMute,
  readOnlyMode = false,
}: TrimmerProps) => {
  const [segments, setSegments] = useState<Segment[]>([
    { id: "1", start: trimStart, end: trimEnd, name: "Segment 1" },
  ]);
  const [selectedSegmentId, setSelectedSegmentId] = useState("1");
  const [drag, setDrag] = useState<null | {
    type: "start" | "end" | "knob" | "move";
    startX: number;
    dragstart: number;
    origStart: number;
    origEnd: number;
  }>(null);
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [playingSegmentId, setPlayingSegmentId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredSegmentBarId, setHoveredSegmentBarId] = useState<string | null>(
    null
  );
  const [hoveredSlateType, setHoveredSlateType] = useState<
    "pre" | "post" | null
  >(null);

  const progressDivRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const playTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hoverCardRef = useRef<HTMLDivElement>(null);
  const slateHoverCardRef = useRef<HTMLDivElement>(null);
  const seekbarClickedRef = useRef(false);
  const [playbackPhase, setPlaybackPhase] = useState<
    "pre-slate" | "video-segments" | "post-slate" | null
  >(null);
  const [slateStartTime, setSlateStartTime] = useState(0);
  const [currentSlateOverlay, setCurrentSlateOverlay] =
    useState<SlateOverlay | null>(null);
  const slateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentSegmentIndexRef = useRef(0); // Add ref to track current segment index
  const dropdownToggleRef = useRef(false); // Prevent rapid dropdown toggling

  const trimmerLocation = getContentLocation();

  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);
  const preRollSlate = slateOverlays.find(
    (slate) => slate.isPreRoll && slate.visible
  );
  const postRollSlate = slateOverlays.find(
    (slate) => slate.isPostRoll && slate.visible
  );

  useClickOutside(hoverCardRef, (event) => {
    const target = event.target as HTMLElement;
    if (!target.closest(`[data-segment-id="${hoveredSegmentBarId}"]`)) {
      setHoveredSegmentBarId(null);
    }
  });

  useClickOutside(slateHoverCardRef, (event) => {
    const target = event.target as HTMLElement;
    if (!target.closest(`[data-slate-type="${hoveredSlateType}"]`)) {
      setHoveredSlateType(null);
    }
  });

  useEffect(() => {
    setSegments((prev) =>
      prev.map((s) =>
        s.id === selectedSegmentId
          ? { ...s, start: trimStart, end: trimEnd }
          : s
      )
    );
  }, [trimStart, trimEnd]);

  const updateSegment = (segmentId: string, updates: Partial<Segment>) => {
    // Stop preview if any segment is being modified during playback
    if (isPlayingAll) {
      stopAllSegments();
    }
    setSegments((prev) =>
      prev.map((s) => (s.id === segmentId ? { ...s, ...updates } : s))
    );
  };

  const constrainTime = (time: number, min: number, max: number) => {
    return Math.min(Math.max(min, time), max);
  };

  const handleTrim = (start: number, end: number) => {
    OnTrim(start, end);
  };

  const seekVideo = (time: number, resetFlags = true) => {
    playerRef.current?.seek(time);
    if (resetFlags) {
      resetUserInteractionFlags();
    }
  };

  // Helper function to handle play/pause
  const togglePlayPause = () => {
    if (pauseIcon) {
      // Resume playback from current position
      playerRef.current?.play();
      setPauseIcon(false);
    } else {
      // Pause playback
      playerRef.current?.pause();
      setPauseIcon(true);
    }
    if (selectedSegment && playedDuration >= selectedSegment.end) {
      // resetUserInteractionFlags();
      playerRef.current?.rePlay();
      playerRef.current?.pause();
      setPauseIcon(true);
    }
  };

  const handleSeek = (offset: number) => {
    const newTime = constrainTime(playedDuration + offset, 0, totalDuration);
    seekVideo(newTime, selectedSegment && newTime <= selectedSegment.end);
  };

  // Helper function to render control button
  const renderControlButton = (
    onClick: () => void,
    icon: React.ReactNode,
    className: string,
    title?: string
  ) => (
    <button onClick={onClick} className={className} title={title}>
      {icon}
    </button>
  );

  // Helper function to render time input section
  const renderTimeInputSection = (type: "start" | "end") => {
    if (!selectedSegment) return null;

    const value =
      type === "start" ? selectedSegment.start : selectedSegment.end;
    const otherValue =
      type === "start" ? selectedSegment.end : selectedSegment.start;
    const isStart = type === "start";

    const handleChange = (v: number) => {
      const constrainedValue = isStart
        ? constrainTime(v, 0, selectedSegment.end - 1)
        : constrainTime(v, selectedSegment.start + 1, totalDuration);

      updateSegment(selectedSegmentId, { [type]: constrainedValue });
      handleTrim(
        isStart ? constrainedValue : selectedSegment.start,
        isStart ? selectedSegment.end : constrainedValue
      );
    };

    const handleSeek = (seconds: number) => {
      const constrainedValue = isStart
        ? constrainTime(seconds, 0, selectedSegment.end - 1)
        : constrainTime(seconds, selectedSegment.start + 1, totalDuration);

      updateSegment(selectedSegmentId, { [type]: constrainedValue });
      handleTrim(
        isStart ? constrainedValue : selectedSegment.start,
        isStart ? selectedSegment.end : constrainedValue
      );
    };

    return (
      <div className={styles.timeInputs}>
        <span>{type === "start" ? "Start" : "End"}</span>
        <TimeInput
          value={value}
          onChange={handleChange}
          totalTime={totalDuration}
          isStart={isStart}
          secondTime={otherValue}
          isUpdatingTrimTime={false}
          setIsUpdatingTrimTime={noop}
          seekVideo={handleSeek}
        />
      </div>
    );
  };

  const renderVideoControls = () => (
    <div className={styles.videoControls}>
      {renderControlButton(
        () => handleSeek(-10),
        <Seek1Icon style={{ transform: "scaleX(-1)" }} />,
        styles.seekBtn
      )}
      {renderControlButton(
        () => handleSeek(-5),
        <Seek2Icon style={{ transform: "scaleX(-1)" }} />,
        styles.seekBtn
      )}
      {/* {renderControlButton(
        () => setSegmentBoundary('start'),
        <PointerIcon style={{ transform: 'scaleX(-1)' }} />,
        styles.setTimeBtn,
        'Set Start to Playhead',
      )} */}
      {renderControlButton(
        togglePlayPause,
        pauseIcon ? <PlayIcon /> : <PauseIcon />,
        styles.playPauseBtn
      )}
      {/* {renderControlButton(() => setSegmentBoundary('end'), <PointerIcon />, styles.setTimeBtn, 'Set End to Playhead')} */}
      {renderControlButton(() => handleSeek(5), <Seek2Icon />, styles.seekBtn)}
      {renderControlButton(() => handleSeek(10), <Seek1Icon />, styles.seekBtn)}
    </div>
  );

  const renderSegmentControls = () => {
    return (
      <div
        className={styles.segmentControls}
        style={{ margin: "25px 0 10px 0" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {selectedSegment && renderTimeInputSection("start")}
          {selectedSegment && renderTimeInputSection("end")}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {renderVideoControls()}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div className="btn-group" role="group">
            <button
              onClick={() => {
                trackEvent("browse", {
                  category: "trimmer",
                  entity: "preview",
                  action: "click",
                  location: trimmerLocation,
                  ...getTrackUserContext(),
                });
                if (isPlayingAll) {
                  stopAllSegments();
                } else {
                  playAllSegments();
                }
              }}
              className={`btn ${
                isPlayingAll ? "btn-danger" : "btn-outline-primary"
              }`}
              type="button"
            >
              {isPlayingAll ? "Preview" : "Preview"}
            </button>
            <Dropdown
              align="end"
              onToggle={(isOpen) => {
                // Prevent rapid toggling that can cause ResizeObserver errors
                if (dropdownToggleRef.current) return; // Block rapid toggles

                dropdownToggleRef.current = true;
                setTimeout(() => {
                  dropdownToggleRef.current = false;
                }, 300); // Block toggling for 300ms

                if (isOpen) {
                  // Add a small delay to prevent rapid state changes
                  setTimeout(() => {
                    // Force a layout recalculation to prevent ResizeObserver issues
                    window.dispatchEvent(new Event("resize"));
                  }, 10);
                }
              }}
              autoClose={true}
            >
              <Dropdown.Toggle
                variant="outline-primary"
                className="px-1 border-start-0 no-caret"
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                <ChevronDown size={19} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    // Add a small delay to prevent rapid state changes
                    setTimeout(() => {
                      resetToInitialState();
                    }, 50);
                  }}
                  className="text-capitalize"
                >
                  Reset
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <button
            onClick={handleMute}
            className="btn btn-sm btn-outline-primary pointer"
            title={isMuted ? "Mute" : "Volume"}
            style={{
              padding: "4px 8px",
              minWidth: "32px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isMuted ? (
              <i className="bi-volume-mute fs-4 iconColor" />
            ) : (
              <i className="bi bi-volume-up fs-4 iconColor" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderSegmentManagement = () => {
    if (!allowMultipleSegments) return null;

    return (
      <div className={styles.segmentManagement}>
        <div className={styles.segmentHeader}>
          <div className={styles.segmentControls}>
            {/* Plus icon moved to seekbar line */}
          </div>
        </div>
      </div>
    );
  };

  const handleDrag = (e: MouseEvent) => {
    if (!progressDivRef.current || !selectedSegment) return;

    const { left, width } = progressDivRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - left, 0), width);
    const time = (x / width) * totalDuration;

    if (!drag) return;

    switch (drag.type) {
      case "start": {
        const idx = segments.findIndex((s) => s.id === selectedSegmentId);
        const prevEnd =
          allowMultipleSegments && idx > 0 ? segments[idx - 1].end : 0;
        const newStart = constrainTime(time, prevEnd, selectedSegment.end - 1);
        updateSegment(selectedSegmentId, { start: newStart });
        handleTrim(newStart, selectedSegment.end);
        seekVideo(newStart, false);
        break;
      }
      case "end": {
        const idx = segments.findIndex((s) => s.id === selectedSegmentId);
        const nextStart =
          allowMultipleSegments && idx < segments.length - 1
            ? segments[idx + 1].start
            : totalDuration;
        const newEnd = constrainTime(
          time,
          selectedSegment.start + 1,
          nextStart
        );
        updateSegment(selectedSegmentId, { end: newEnd });
        handleTrim(selectedSegment.start, newEnd);
        seekVideo(selectedSegment.start, false);
        break;
      }
      case "move": {
        const idx = segments.findIndex((s) => s.id === selectedSegmentId);
        const prevEnd =
          allowMultipleSegments && idx > 0 ? segments[idx - 1].end : 0;
        const nextStart =
          allowMultipleSegments && idx < segments.length - 1
            ? segments[idx + 1].start
            : totalDuration;
        const duration = selectedSegment.end - selectedSegment.start;
        const offset = time - drag.dragstart;
        let newStart = constrainTime(
          drag.origStart + offset,
          0,
          totalDuration - duration
        );
        let newEnd = newStart + duration;

        if (allowMultipleSegments) {
          if (newStart < prevEnd) {
            newStart = prevEnd;
            newEnd = newStart + duration;
          }
          if (newEnd > nextStart) {
            newEnd = nextStart;
            newStart = newEnd - duration;
          }
        }
        updateSegment(selectedSegmentId, { start: newStart, end: newEnd });
        handleTrim(newStart, newEnd);
        seekVideo(newStart, false);
        break;
      }
      case "knob":
        seekVideo(time, false);
        break;
    }
  };

  const handleSegmentClick = (segment: Segment, e: React.MouseEvent) => {
    // Prevent progress bar click handler from firing when clicking a segment
    e.stopPropagation();
    if (hoveredSegmentBarId === segment.id) {
      setHoveredSegmentBarId(null);
    } else {
      setHoveredSegmentBarId(segment.id);
      ensureKnobInSegment(segment);
    }
  };

  const handleSegmentMouseDown = (segment: Segment, e: React.MouseEvent) => {
    if (!allowMultipleSegments || e.target !== e.currentTarget) return;
    setIsDragging(true);
    setSelectedSegmentId(segment.id);
    playerRef.current?.pause();
    setPauseIcon(true);
    setDrag({
      type: "move",
      startX: e.clientX,
      dragstart:
        ((e.clientX - progressDivRef.current!.getBoundingClientRect().left) /
          progressDivRef.current!.getBoundingClientRect().width) *
        totalDuration,
      origStart: segment.start,
      origEnd: segment.end,
    });
  };

  const renderSegment = (segment: Segment) => {
    const isHovered = hoveredSegmentBarId === segment.id;
    const isSelected = selectedSegmentId === segment.id;

    const effectiveDuration = originalVideoDuration || totalDuration || 100;

    return (
      <div
        key={segment.id}
        data-segment-id={segment.id}
        className={`${styles.segmentBar} ${
          isSelected ? styles.selected : styles.unselected
        } ${!allowMultipleSegments ? styles.singleMode : ""}`}
        style={{
          left: `${(segment.start / effectiveDuration) * 100}%`,
          width: `${
            ((segment.end - segment.start) / effectiveDuration) * 100
          }%`,
        }}
        onMouseDown={(e) => handleSegmentMouseDown(segment, e)}
        onClick={(e) => handleSegmentClick(segment, e)}
      >
        {isHovered && (
          <div
            ref={hoverCardRef}
            data-segment-id={segment.id}
            className={`${styles.trimmerHoverCard} ${styles.hoverCard} ${
              isSelected ? styles.selected : ""
            }`}
          >
            <div className="d-flex p-3 w-100">
              <span className="me-auto">
                In: {secondsToHms(segment.start)}{" "}
              </span>
              <span className="ms-auto">Out: {secondsToHms(segment.end)}</span>
            </div>
            <div className="d-flex w-100 border-top">
              <div className={styles.hoverCardContent}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    playSegment(segment);
                  }}
                  // className={styles.playBtn}
                  className="btn btn-outline-secondary border-0 me-auto w-100 border-end py-2"
                >
                  Play
                </button>
                {segments.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSegment(segment.id);
                    }}
                    // className={styles.deleteBtn}
                    className="btn btn-outline-secondary border-0 ms-auto w-100 py-2"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTrimHandles = () => {
    if (!selectedSegment) return null;

    const effectiveDuration = originalVideoDuration || totalDuration || 100;

    // In readonly mode, only show the red dot (knob) for playhead
    if (readOnlyMode || isPreset) {
      return (
        <div
          ref={knobRef}
          className={styles.knob}
          style={{ left: `${(playedDuration / effectiveDuration) * 100}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            playerRef.current?.pause();
            setPauseIcon(true);
            setDrag({
              type: "knob",
              startX: e.clientX,
              dragstart: 0,
              origStart: 0,
              origEnd: 0,
            });
          }}
        />
      );
    }

    return (
      <>
        <StartTrim
          className={`${styles.trimPointer} ${styles.trimHandle}`}
          style={{
            left: `${(selectedSegment.start / effectiveDuration) * 100}%`,
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            playerRef.current?.pause();
            setPauseIcon(true);
            setDrag({
              type: "start",
              startX: e.clientX,
              dragstart: selectedSegment.start,
              origStart: selectedSegment.start,
              origEnd: selectedSegment.end,
            });
          }}
        />
        <EndTrim
          className={`${styles.trimPointer} ${styles.trimHandle}`}
          style={{
            left: `${(selectedSegment.end / effectiveDuration) * 100}%`,
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            playerRef.current?.pause();
            setPauseIcon(true);
            setDrag({
              type: "end",
              startX: e.clientX,
              dragstart: selectedSegment.end,
              origStart: selectedSegment.start,
              origEnd: selectedSegment.end,
            });
          }}
        />
        <div
          ref={knobRef}
          className={styles.knob}
          style={{ left: `${(playedDuration / effectiveDuration) * 100}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            playerRef.current?.pause();
            setPauseIcon(true);
            setDrag({
              type: "knob",
              startX: e.clientX,
              dragstart: 0,
              origStart: 0,
              origEnd: 0,
            });
          }}
        />
      </>
    );
  };

  // Helper function to handle progress bar click
  const handleProgressClick = (e: React.MouseEvent) => {
    if (isDragging || !progressDivRef.current) return;

    const { left, width } = progressDivRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - left, 0), width);

    const effectiveDuration = totalDuration > 0 ? totalDuration : 100;
    const time = (x / width) * effectiveDuration;

    seekbarClickedRef.current = true;
    setHasUserDragged.current = true;
    seekVideo(time, false);

    if (!allowMultipleSegments) {
      setTimeout(() => {
        seekbarClickedRef.current = false;
      }, 1000);
    } else {
      if (selectedSegment && time > selectedSegment.end) {
        setTimeout(() => {
          playerRef.current?.play();
          setPauseIcon(false);
          setIsPlayingAll(false);
          setPlayingSegmentId(null);
        }, 50);
      } else {
        setTimeout(() => {
          seekbarClickedRef.current = false;
          setHasUserDragged.current = false;
        }, 1000);
      }
    }
  };

  useEffect(() => {
    if (!drag || !selectedSegment) return;
    setIsDragging(true);

    const move = handleDrag;
    const up = () => {
      setDrag(null);
      setTimeout(() => setIsDragging(false), 10);
      resetUserInteractionFlags();
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [
    drag,
    selectedSegmentId,
    totalDuration,
    playerRef,
    OnTrim,
    selectedSegment,
    segments,
    allowMultipleSegments,
    trimmerLocation,
  ]);

  useEffect(() => {
    if (!progressRef.current || !knobRef.current) return;

    if (playbackPhase !== "video-segments") {
      return;
    }

    if (!isPlayingAll || playbackPhase !== "video-segments") {
      return;
    }

    const effectiveDuration = originalVideoDuration || totalDuration || 100;
    const percent = Math.min(playedDuration / effectiveDuration, 1);
    progressRef.current.style.transform = `scaleX(${percent})`;
    knobRef.current.style.left = `${percent * 100}%`;
  }, [
    playedDuration,
    totalDuration,
    playbackPhase,
    isPlayingAll,
    originalVideoDuration,
  ]);
  useEffect(() => {
    if (!isPlayingAll || playbackPhase !== "video-segments") {
      return;
    }

    const currentSegment = segments[currentSegmentIndexRef.current];
    if (!currentSegment) {
      return;
    }

    if (playedDuration < 0.5) {
      return;
    }

    const checkSegmentEnd = () => {
      if (playedDuration >= currentSegment.end) {
        if (currentSegmentIndexRef.current < segments.length - 1) {
          currentSegmentIndexRef.current += 1;
          setCurrentSegmentIndex(currentSegmentIndexRef.current);
          const nextSegment = segments[currentSegmentIndexRef.current];

          setSelectedSegmentId(nextSegment.id);
          playerRef.current?.seek(nextSegment.start);
          playerRef.current?.play();
          setPauseIcon(false);
        } else {
          if (postRollSlate || postRollSlateId) {
            if (postRollSlateId && onSequentialPlayback) {
              onSequentialPlayback({
                type: "post-slate",
                slateId: postRollSlateId,
              });
            }
            setPlaybackPhase("post-slate");
            if (postRollSlate) {
              setCurrentSlateOverlay(postRollSlate);
            } else {
              setCurrentSlateOverlay(null);
            }
            setSlateStartTime(Date.now());
            playerRef.current?.pause();
            setPauseIcon(true);
          } else {
            setIsPlayingAll(false);
            currentSegmentIndexRef.current = 0;
            setCurrentSegmentIndex(0);
            setPlaybackPhase(null);
            setCurrentSlateOverlay(null);
            playerRef.current?.pause();
            setPauseIcon(true);
          }
        }
      } else if (
        playedDuration >= currentSegment.end - 0.5 &&
        currentSegmentIndexRef.current >= segments.length - 1
      ) {
        if (postRollSlate || postRollSlateId) {
          if (postRollSlateId && onSequentialPlayback) {
            onSequentialPlayback({
              type: "post-slate",
              slateId: postRollSlateId,
            });
          }
          setPlaybackPhase("post-slate");
          if (postRollSlate) {
            setCurrentSlateOverlay(postRollSlate);
          } else {
            setCurrentSlateOverlay(null);
          }
          setSlateStartTime(Date.now());
          playerRef.current?.pause();
          setPauseIcon(true);
        }
      }
    };

    checkSegmentEnd();
  }, [
    playedDuration,
    isPlayingAll,
    playbackPhase,
    segments,
    postRollSlate,
    postRollSlateId,
    onSequentialPlayback,
  ]);

  // Add a separate effect to enforce segment boundaries more aggressively
  useEffect(() => {
    if (!isPlayingAll || playbackPhase !== "video-segments") {
      return;
    }

    const currentSegment = segments[currentSegmentIndexRef.current];
    if (!currentSegment) {
      return;
    }

    if (
      playedDuration < currentSegment.start - 1 ||
      playedDuration > currentSegment.end + 1
    ) {
      playerRef.current?.seek(currentSegment.start);
    }
  }, [playedDuration, isPlayingAll, playbackPhase, segments]);

  useEffect(() => {
    if (!playingSegmentId) return;
    const seg = segments.find((s) => s.id === playingSegmentId);
    if (seg && playedDuration >= seg.end - 0.1) {
      playerRef.current?.pause();
      setPauseIcon(true);
      setPlayingSegmentId(null);
      playerRef.current?.seek(seg.end);
    }
  }, [playedDuration, playingSegmentId, segments, playerRef, setPauseIcon]);

  useEffect(() => {
    return () => {
      if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (playedDuration >= totalDuration - 0.1) {
      seekbarClickedRef.current = false;
      if (allowMultipleSegments) {
        setHasUserDragged.current = false;
      }

      if (
        isPlayingAll &&
        playbackPhase === "video-segments" &&
        (postRollSlate || postRollSlateId)
      ) {
        if (postRollSlateId && onSequentialPlayback) {
          onSequentialPlayback({
            type: "post-slate",
            slateId: postRollSlateId,
          });
        }
        setPlaybackPhase("post-slate");
        if (postRollSlate) {
          setCurrentSlateOverlay(postRollSlate);
        }
        setSlateStartTime(Date.now());
        playerRef.current?.pause();
        setPauseIcon(true);
      }
    }
  }, [
    playedDuration,
    totalDuration,
    allowMultipleSegments,
    isPlayingAll,
    playbackPhase,
    postRollSlate,
    postRollSlateId,
    onSequentialPlayback,
  ]);

  useEffect(() => {
    if (onSegmentsChange) {
      onSegmentsChange(segments);
    }
  }, [segments]);

  useEffect(() => {
    if (isPlayingAll && slateOverlays) {
      stopAllSegments();
    }
  }, [slateOverlays]);

  useEffect(() => {
    if (!isPlayingAll) {
      setPlaybackPhase(null);
      setCurrentSlateOverlay(null);
    }
  }, [slateOverlays, isPlayingAll]);

  useEffect(() => {
    if (!isPlayingAll || !playbackPhase) {
      if (slateTimerRef.current) {
        clearInterval(slateTimerRef.current);
        slateTimerRef.current = null;
      }
      return;
    }

    if (playbackPhase !== "pre-slate" && playbackPhase !== "post-slate") {
      if (slateTimerRef.current) {
        clearInterval(slateTimerRef.current);
        slateTimerRef.current = null;
      }
      return;
    }

    const checkSlateTiming = () => {
      const currentTime = Date.now() - slateStartTime;
      const currentTimeSeconds = currentTime / 1000;
      if (playbackPhase === "pre-slate" && preRollSlate) {
        const rawDuration = preRollSlate.duration;
        const slateDuration =
          typeof rawDuration === "string"
            ? hmsToSeconds(rawDuration)
            : Number(rawDuration) || 5;
        if (currentTimeSeconds >= slateDuration) {
          if (segments.length > 0) {
            if (slateTimerRef.current) {
              clearInterval(slateTimerRef.current);
              slateTimerRef.current = null;
            }

            const sortedSegments = [...segments].sort(
              (a, b) => a.start - b.start
            );
            setPlaybackPhase("video-segments");
            setCurrentSlateOverlay(null);
            setIsPlayingAll(true);
            setCurrentSegmentIndex(0);
            currentSegmentIndexRef.current = 0;
            setSelectedSegmentId(sortedSegments[0].id);
            if (onSequentialPlayback) {
              onSequentialPlayback({
                type: "video-segments",
                segments: sortedSegments,
              });
            }

            const firstSegment = sortedSegments[0];
            playerRef.current?.pause();
            setPauseIcon(true);
            playerRef.current?.seek(firstSegment.start);
            playerRef.current?.play();
            setPauseIcon(false);
          } else if (postRollSlate || postRollSlateId) {
            if (postRollSlateId && onSequentialPlayback) {
              onSequentialPlayback({
                type: "post-slate",
                slateId: postRollSlateId,
              });
            }
            setPlaybackPhase("post-slate");
            if (postRollSlate) {
              setCurrentSlateOverlay(postRollSlate);
            }
            setSlateStartTime(Date.now());
          } else {
            setIsPlayingAll(false);
            setPlaybackPhase(null);
            setCurrentSlateOverlay(null);
            setCurrentSegmentIndex(0);
            if (slateTimerRef.current) {
              clearInterval(slateTimerRef.current);
              slateTimerRef.current = null;
            }
          }
        }
      } else if (
        playbackPhase === "post-slate" &&
        (postRollSlate || postRollSlateId)
      ) {
        const rawDuration = postRollSlate?.duration || 5;
        const slateDuration =
          typeof rawDuration === "string"
            ? hmsToSeconds(rawDuration)
            : Number(rawDuration) || 5;
        if (currentTimeSeconds >= slateDuration) {
          setIsPlayingAll(false);
          setPlaybackPhase(null);
          setCurrentSlateOverlay(null);
          setCurrentSegmentIndex(0);
          currentSegmentIndexRef.current = 0;

          if (segments.length > 0) {
            setSelectedSegmentId(segments[0].id);
          }
          if (slateTimerRef.current) {
            clearInterval(slateTimerRef.current);
            slateTimerRef.current = null;
          }
        }
      }
    };

    if (!slateTimerRef.current) {
      slateTimerRef.current = setInterval(checkSlateTiming, 100);
    }
    return () => {
      // Only clear timer on unmount or when dependencies change significantly
      // Don't clear it on every render to avoid race conditions
    };
  }, [
    isPlayingAll,
    playbackPhase,
    slateStartTime,
    preRollSlate,
    postRollSlate,
    segments,
    playerRef,
    setPauseIcon,
  ]);

  useEffect(() => {
    return () => {
      if (slateTimerRef.current) {
        clearInterval(slateTimerRef.current);
        slateTimerRef.current = null;
      }
    };
  }, []);

  const parseDurationToNumber = (duration: any): number => {
    if (typeof duration === "number") {
      return duration;
    }
    if (typeof duration === "string") {
      const parts = duration.split(":");
      if (parts.length === 2) {
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        return minutes * 60 + seconds;
      }
      // Try to parse as a number
      const parsed = parseFloat(duration);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const calculateTotalDuration = () => {
    let totalDuration = 0;

    // Add pre-roll slate duration
    const preRollSlate = canvasOverlay?.slateOverlays.find(
      (slate: any) => slate?.isPreRoll && slate?.visible
    );
    if (preRollSlate) {
      totalDuration += parseDurationToNumber(preRollSlate.duration) || 5;
    } else if (preRollMedia) {
      totalDuration +=
        parseDurationToNumber(preRollMedia?.duration) ||
        parseDurationToNumber(preRollMedia?.meta?.duration) ||
        5;
    }

    // Add video segments duration
    if (segments && segments.length > 0) {
      const videoDuration = segments.reduce(
        (sum: number, segment: any) => sum + (segment.end - segment.start),
        0
      );
      totalDuration += videoDuration;
    } else {
      const currentDuration =
        duration && duration > 0 ? duration : item?.meta?.duration || 0;
      totalDuration += currentDuration;
    }

    // Add post-roll slate duration
    const postRollSlate = canvasOverlay?.slateOverlays?.find(
      (slate: any) => slate.isPostRoll && slate.visible
    );
    if (postRollSlate) {
      totalDuration += parseDurationToNumber(postRollSlate.duration) || 5;
    } else if (postRollMedia) {
      totalDuration +=
        parseDurationToNumber(postRollMedia?.duration) ||
        parseDurationToNumber(postRollMedia?.meta?.duration) ||
        5;
    }
    if (getTotalDuration) {
      getTotalDuration(totalDuration);
    }

    return totalDuration;
  };

  const resetUserInteractionFlags = () => {
    seekbarClickedRef.current = false;
    if (allowMultipleSegments) {
      setHasUserDragged.current = false;
    }
  };

  const resetToInitialState = () => {
    // Reset segments to initial state (whole video)
    const initialSegment = {
      id: "1",
      start: 0,
      end: totalDuration,
      name: "Segment 1",
    };
    setSegments([initialSegment]);
    setSelectedSegmentId("1");

    // Reset trim to full video
    OnTrim(0, totalDuration);

    // Reset playback state
    setIsPlayingAll(false);
    setCurrentSegmentIndex(0);
    setPlayingSegmentId(null);
    setPlaybackPhase(null);
    setCurrentSlateOverlay(null);

    // Seek to beginning
    playerRef.current?.seek(0);
    playerRef.current?.pause();
    setPauseIcon(true);

    // Clear any timers
    if (slateTimerRef.current) {
      clearInterval(slateTimerRef.current);
      slateTimerRef.current = null;
    }

    // Call parent reset if provided
    onReset?.();

    // Reset user interaction flags
    resetUserInteractionFlags();
  };

  const addSegment = () => {
    if (!allowMultipleSegments) return;

    const newId = Date.now().toString();
    const lastSegment = segments.reduce<Segment | null>(
      (latest, seg) => (!latest || seg.end > latest.end ? seg : latest),
      null
    );
    const start = lastSegment ? lastSegment.end + 5 : 0;
    const end = Math.min(start + 10, totalDuration);
    if (start >= totalDuration) return;
    setSegments((prev) => [
      ...prev,
      {
        id: newId,
        start,
        end,
        name: `Segment ${prev.length + 1}`,
      },
    ]);
    setSelectedSegmentId(newId);
  };

  const deleteSegment = (id: string) => {
    if (!allowMultipleSegments || segments.length <= 1) return;
    setSegments((prev) => {
      const newSegments = prev.filter((s) => s.id !== id);
      if (newSegments.length > 0) {
        setSelectedSegmentId(newSegments[0].id);
      } else {
        setSelectedSegmentId("");
      }
      return newSegments;
    });
  };

  const playSegment = (segment: Segment) => {
    ensureKnobInSegment(segment);
    playerRef.current?.seek(segment.start);
    resetUserInteractionFlags();

    setTimeout(() => {
      playerRef.current?.play();
      setPauseIcon(false);
      setIsPlayingAll(false);
      setPlayingSegmentId(segment.id);
    }, 50);
  };

  const ensureKnobInSegment = (segment: Segment) => {
    if (playedDuration < segment.start || playedDuration > segment.end) {
      playerRef.current?.seek(segment.start);
    }
  };

  const playAllSegments = () => {
    if (
      segments.length === 0 &&
      !preRollSlate &&
      !postRollSlate &&
      !postRollSlateId
    ) {
      return;
    }
    if (slateTimerRef.current) {
      clearInterval(slateTimerRef.current);
      slateTimerRef.current = null;
    }

    setIsPlayingAll(false);
    setCurrentSegmentIndex(0);
    currentSegmentIndexRef.current = 0;
    setPlayingSegmentId(null);
    setPlaybackPhase(null);
    setCurrentSlateOverlay(null);

    currentSegmentIndexRef.current = 0;
    if (segments.length > 0) {
      setSelectedSegmentId(segments[0].id);
    } else {
      setSelectedSegmentId("");
    }

    setIsPlayingAll(true);
    setSlateStartTime(Date.now());

    if (preRollSlate) {
      if (onSequentialPlayback) {
        onSequentialPlayback({ type: "pre-slate", slateId: preRollSlateId });
      }
      setPlaybackPhase("pre-slate");
      setCurrentSlateOverlay(preRollSlate);
    } else if (segments.length > 0) {
      const sortedSegments = [...segments].sort((a, b) => a.start - b.start);

      if (onSequentialPlayback) {
        onSequentialPlayback({
          type: "video-segments",
          segments: sortedSegments,
        });
      }
      setPlaybackPhase("video-segments");
      setCurrentSlateOverlay(null);
      setIsPlayingAll(true);
      setCurrentSegmentIndex(0);
      currentSegmentIndexRef.current = 0;

      currentSegmentIndexRef.current = 0;
      setSelectedSegmentId(sortedSegments[0].id);

      const firstSegment = sortedSegments[0];
      playerRef.current?.seek(firstSegment.start);
      playerRef.current?.play();
      setPauseIcon(false);
    } else if (postRollSlate || postRollSlateId) {
      if (onSequentialPlayback) {
        onSequentialPlayback({ type: "video-segments", segments: [] });

        if (postRollSlateId && onSequentialPlayback) {
          onSequentialPlayback({
            type: "post-slate",
            slateId: postRollSlateId,
          });
        }
        setPlaybackPhase("post-slate");
        if (postRollSlate) {
          setCurrentSlateOverlay(postRollSlate);
        } else {
          setCurrentSlateOverlay(null);
        }
        setSlateStartTime(Date.now());
      } else {
        setPlaybackPhase("post-slate");
        if (postRollSlate) {
          setCurrentSlateOverlay(postRollSlate);
        } else {
          setCurrentSlateOverlay(null);
        }
        setSlateStartTime(Date.now());
      }
    }

    resetUserInteractionFlags();
  };

  const stopAllSegments = () => {
    setIsPlayingAll(false);
    setCurrentSegmentIndex(0);
    currentSegmentIndexRef.current = 0;
    setPlaybackPhase(null);
    setCurrentSlateOverlay(null);
    playerRef.current?.pause();
    setPauseIcon(true);

    if (segments.length > 0) {
      setSelectedSegmentId(segments[0].id);
    }

    if (slateTimerRef.current) {
      clearInterval(slateTimerRef.current);
      slateTimerRef.current = null;
    }
    if (onSequentialPlayback) {
      onSequentialPlayback({
        type: "video-segments",
        segments: [],
        reset: true,
      });
    }

    resetUserInteractionFlags();
  };

  const renderSlateIndicator = (type: "pre" | "post", slate?: SlateOverlay) => {
    const isPlaying = playbackPhase === `${type}-slate`;
    const hasSlate = slate && slate.visible;

    let backgroundColor = "#ddd";
    if (hasSlate) {
      backgroundColor = isPlaying ? "#ffc10780" : "#ffc107";
    }

    // Calculate progress for red bar when slate is playing
    let progressWidth = 0;
    if (isPlaying && slate) {
      const currentTime = Date.now() - slateStartTime;
      const currentTimeSeconds = currentTime / 1000;
      const rawDuration = slate.duration;
      const slateDuration =
        typeof rawDuration === "string"
          ? hmsToSeconds(rawDuration)
          : Number(rawDuration) || 5;
      progressWidth = Math.min((currentTimeSeconds / slateDuration) * 100, 100);
    }

    return (
      <div
        className={`position-relative rounded-1 cursor-default ${styles.slateIndicator}`}
        data-slate-type={type}
        style={{
          width: "60px",
          height: "10px",
          backgroundColor: backgroundColor,
          animation: isPlaying ? "pulse 1s infinite" : "none",
        }}
        title={
          hasSlate
            ? `${type === "pre" ? "Pre" : "Post"}-roll Slate (${
                slate.duration || 5
              }s)${isPlaying ? " - PLAYING" : ""}`
            : `No ${type === "pre" ? "Pre" : "Post"}-roll Slate`
        }

        // onClick={(e) => {
        //   e.stopPropagation();
        //   if (!hasSlate) return;
        //   if (hoveredSlateType === type) {
        //     setHoveredSlateType(null);
        //   } else {
        //     setHoveredSlateType(type);
        //   }
        // }}
      >
        {/* Red progress bar overlay when slate is playing */}
        {isPlaying && (
          <div
            style={{
              width: `${progressWidth}%`,
              backgroundColor: "#dc3545", // Red color
            }}
            className="position-absolute top-0 left-0  h-100%  rounded-2 transition-width duration-100 ease-out bg-danger"
          />
        )}
      </div>
    );
  };

  const getMainSegmentAreaStyle = () => {
    return {
      position: "absolute" as const,
      left: "0%",
      width: "100%",
      height: "100%",
    };
  };

  return (
    <div className={styles.trimmerContainer}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      {renderSegmentManagement()}
      <div
        style={{
          width: "100%",
        }}
        className="position-relative d-flex align-items-center gap-2 w-100%"
      >
        {isPreset && (
          <button
            onClick={() => {
              trackEvent("browse", {
                category: "trimmer",
                entity: "preview",
                action: "click",
                location: trimmerLocation,
                ...getTrackUserContext(),
              });
              if (isPlayingAll) {
                stopAllSegments();
              } else {
                playAllSegments();
              }
            }}
            className={`btn btn-sm ${
              isPlayingAll ? "btn-outline-danger" : "btn-outline-primary"
            }`}
            style={{
              padding: "4px 8px",
              fontSize: "12px",
              minWidth: "32px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={isPlayingAll ? "Pause Preview" : "Play Preview"}
          >
            {isPlayingAll ? (
              <i className="bi bi-pause-fill" />
            ) : (
              <i className="bi bi-play-fill fs-4" />
            )}
          </button>
        )}
        <div className="d-flex align-items-center flex-grow-1">
          <div
            className="slateCont d-flex justify-content-center align-items-center"
            style={{
              width: "70px",
              height: "10px",
            }}
          >
            {renderSlateIndicator("pre", preRollSlate)}
          </div>
          <div
            ref={progressDivRef}
            className={styles.progressContainer}
            onClick={handleProgressClick}
            style={{
              flex: 1,
              position: "relative",
            }}
          >
            <div className={styles.progressBar} />

            <div style={getMainSegmentAreaStyle()}>
              {(allowMultipleSegments || isPlayingAll
                ? segments
                : segments.filter((s) => s.id === selectedSegmentId)
              ).map(renderSegment)}
              {renderTrimHandles()}
            </div>
          </div>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              width: "70px",
              height: "10px",
            }}
          >
            {renderSlateIndicator("post", postRollSlate)}
          </div>
        </div>
        {allowMultipleSegments && !isPreset && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              width: "40px",
              height: "4px",
            }}
          >
            <button onClick={addSegment} className="btn btn-outline-primary">
              <PlusIcon size={18} />
            </button>
          </div>
        )}
        {isPreset && (
          <>
            <button
              onClick={handleMute}
              className="btn btn-sm btn-outline-primary pointer d-flex align-items-center justify-content-center"
              title={isMuted ? "Mute" : "Volume"}
              style={{
                padding: "4px 8px",
                minWidth: "32px",
                height: "24px",
              }}
            >
              {isMuted ? (
                <i className="bi-volume-mute fs-4 iconColor" />
              ) : (
                <i className="bi bi-volume-up fs-4 iconColor" />
              )}
            </button>
          </>
        )}
      </div>
      <div
        className="d-flex align-items-center position-absolute"
        style={{ left: "14px", width: "calc(100% - 82px)", marginTop: "-8px" }}
      >
        <span style={{ marginLeft: isPreset ? "60px" : "0px" }}>
          [Pre Slate]
        </span>
        <span
          className="ms-auto"
          style={{ marginLeft: isPreset ? "100px" : "0px" }}
        >
          [Post Slate]
        </span>
      </div>

      <div
        className="d-flex align-items-center position-absolute H"
        style={{
          left: isPreset ? "40px" : "16px",
          width: "calc(100% - 82px)",
          marginTop: "-8px",
        }}
      >
        <span style={{ marginLeft: isPreset ? "30px" : "0px" }}>
          {isPreset ? "[Pre Slate]" : "00:00"}{" "}
        </span>
        <span
          className="ms-auto"
          style={{ marginLeft: isPreset ? "100px" : "0px" }}
        >
          {isPreset
            ? "[Post Slate]"
            : `${secondsToHms(calculateTotalDuration())}`}
        </span>
      </div>

      {!isPreset && renderSegmentControls()}
    </div>
  );
};

export default Trimmer;
